/**
 * Conexão com a sala: abre o WebSocket, se apresenta, reconecta sozinha
 * quando a internet cai e mantém o relógio alinhado com o do servidor.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameEvent } from '@tigrinho/engine';
import { CARD_READ_MS, ORDER_DRAW_MS, ROLL_MS, splitAtCard, walkMs } from '@tigrinho/server/protocol';
import type { ClientAction, ClientMsg, PublicRoom, ServerMsg } from '@tigrinho/server/protocol';
import { deviceToken } from '../lib/storage';

export type Status = 'connecting' | 'online' | 'reconnecting' | 'closed';

export interface RoomError {
  code: string;
  message: string;
  at: number;
}

export interface Listeners {
  onEvents?: (events: GameEvent[], room: PublicRoom) => void;
  /** Os dados começaram a rolar na tela (o resultado só aparece depois da animação). */
  onRoll?: (player: string) => void;
  onTaunt?: (m: Extract<ServerMsg, { t: 'taunt' }>) => void;
  onPhrase?: (m: Extract<ServerMsg, { t: 'phrase' }>) => void;
}

export interface RoomConn {
  status: Status;
  room: PublicRoom | null;
  you: string | null;
  error: RoomError | null;
  /** Diferença entre o relógio do servidor e o deste aparelho. */
  offset: number;
  /** Dados rolando agora na tela (o resultado ainda não apareceu). */
  rolling: { player: string; at: number } | null;
  /** Até quando a tela está mostrando o peão andando (janelas e cartas esperam). */
  holdUntil: number;
  /** "Sorteando quem começa…" na tela (início da partida). */
  ordering: number | null;
  send: (msg: ClientMsg) => void;
  act: (action: ClientAction) => void;
  clearError: () => void;
}

export { CARD_READ_MS, EVADE_MS, MAGNET_MS, MST_EXPEL_MS, MST_INVADE_MS, MST_MARCH_MS, ORDER_DRAW_MS, ROLL_MS, STEP_MS, walkMs } from '@tigrinho/server/protocol';

type RoomMsg = Extract<ServerMsg, { t: 'room' }>;

/**
 * Sala "no meio da jogada": o peão já chegou na casa da carta, mas o efeito da carta ainda
 * não aconteceu (dinheiro, propriedades e posições continuam como estavam antes da carta).
 */
function beforeCard(prev: PublicRoom | null, next: PublicRoom, before: GameEvent[]): PublicRoom {
  const pg = prev?.game;
  const fg = next.game;
  if (!pg || !fg) return next;
  const gain: Record<string, number> = {};
  const pos: Record<string, number> = {};
  for (const e of before) {
    if (e.type === 'passedStart') gain[e.player] = (gain[e.player] ?? 0) + e.amount;
    if (e.type === 'moved') pos[e.player] = e.to;
  }
  return {
    ...next,
    game: {
      ...fg,
      props: pg.props,
      championship: pg.championship,
      effects: pg.effects,
      prompt: null,
      debts: [],
      phase: 'endTurn',
      players: fg.players.map((p) => {
        const o = pg.players.find((x) => x.id === p.id);
        if (!o) return p;
        return { ...p, cash: o.cash + (gain[p.id] ?? 0), position: pos[p.id] ?? o.position, inJail: o.inJail, held: o.held, eliminated: o.eliminated };
      }),
    },
  };
}

const HARD_ERRORS = new Set(['game_in_progress', 'room_full', 'removed', 'room_closed', 'bad_claim', 'seat_taken']);

export function useRoom(code: string, name: string, claim: string | null, listeners: Listeners): RoomConn {
  const [status, setStatus] = useState<Status>('connecting');
  const [room, setRoom] = useState<PublicRoom | null>(null);
  const [you, setYou] = useState<string | null>(null);
  const [error, setError] = useState<RoomError | null>(null);
  const [offset, setOffset] = useState(0);
  const [rolling, setRolling] = useState<{ player: string; at: number } | null>(null);
  const [holdUntil, setHoldUntil] = useState(0);
  const [ordering, setOrdering] = useState<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef(listeners);
  listenersRef.current = listeners;
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let pingTimer: ReturnType<typeof setInterval> | undefined;

    // Fila de apresentação: cada jogada aparece na ordem. Os dados rolam antes do resultado,
    // o peão anda antes das janelas e, se cair numa carta, a carta aparece e fica na tela para
    // ser lida; só depois vem o efeito dela (andar para trás, pagar, perder casa...).
    // Se a fila crescer, pula as animações.
    const queue: RoomMsg[] = [];
    let shown: PublicRoom | null = null;
    let busy = false;
    let timers: ReturnType<typeof setTimeout>[] = [];
    const later = (fn: () => void, ms: number) => timers.push(setTimeout(fn, ms));
    const done = () => {
      busy = false;
      pump();
    };
    // Assentos (quem está conectado, quem é o host) sempre os mais novos que chegaram.
    let seats: Pick<PublicRoom, 'seats' | 'hostId'> | null = null;
    const show = (raw: PublicRoom, events: GameEvent[]) => {
      const room = seats ? { ...raw, ...seats } : raw;
      shown = room;
      setRoom(room);
      setOffset(room.serverNow - Date.now());
      if (events.length) listenersRef.current.onEvents?.(events, room);
    };
    const pump = () => {
      if (busy || stopRef.current) return;
      const m = queue.shift();
      if (!m) return;
      busy = true;
      const fast = queue.length >= 4 || document.visibilityState === 'hidden';
      const size = m.room.game?.props.length ?? 32;
      const [before, after] = splitAtCard(m.events);
      const hasCard = before.some((e) => e.type === 'cardDrawn');
      const apply = () => {
        if (fast || !hasCard) {
          show(m.room, m.events);
          const hold = fast ? 0 : walkMs(m.events, size);
          if (hold > 0) {
            setHoldUntil(Date.now() + hold);
            later(done, hold);
          } else done();
          return;
        }
        // 1) anda até a carta e mostra a carta; 2) depois da leitura, o efeito.
        show(beforeCard(shown, m.room, before), before);
        const walk = walkMs(before, size);
        setHoldUntil(Date.now() + walk);
        later(() => {
          show(m.room, after);
          const hold = walkMs(after, size);
          if (hold > 0) {
            setHoldUntil(Date.now() + hold);
            later(done, hold);
          } else done();
        }, walk + CARD_READ_MS);
      };
      if (m.events.some((e) => e.type === 'gameStarted') && !fast) {
        // "Sorteando quem começa…"
        show(m.room, m.events);
        setOrdering(Date.now());
        setHoldUntil(Date.now() + ORDER_DRAW_MS);
        later(() => {
          setOrdering(null);
          done();
        }, ORDER_DRAW_MS);
        return;
      }
      const dice = m.events.find((e) => e.type === 'diceRolled');
      if (dice && dice.type === 'diceRolled' && !fast) {
        setRolling({ player: dice.player, at: Date.now() });
        listenersRef.current.onRoll?.(dice.player);
        later(() => {
          setRolling(null);
          apply();
        }, ROLL_MS);
      } else apply();
    };

    const connect = () => {
      if (stopRef.current) return;
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${location.host}/ws/${code}`);
      wsRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        const hello: ClientMsg = { t: 'hello', token: deviceToken(), name, protocol: 2, ...(claim ? { claim } : {}) };
        ws.send(JSON.stringify(hello));
        clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'ping' }));
        }, 25_000);
      };

      ws.onmessage = (ev) => {
        let msg: ServerMsg;
        try {
          msg = JSON.parse(String(ev.data)) as ServerMsg;
        } catch {
          return;
        }
        switch (msg.t) {
          case 'welcome':
            queue.length = 0;
            timers.forEach(clearTimeout);
            timers = [];
            busy = false;
            setRolling(null);
            setOrdering(null);
            setHoldUntil(0);
            setYou(msg.you);
            seats = null;
            shown = msg.room;
            setRoom(msg.room);
            setOffset(msg.room.serverNow - Date.now());
            setStatus('online');
            setError(null);
            break;
          case 'room':
            // Quem entrou, saiu ou voltou aparece na hora, mesmo com animações na fila.
            seats = { seats: msg.room.seats, hostId: msg.room.hostId };
            if (busy && shown) {
              shown = { ...shown, ...seats };
              setRoom(shown);
            }
            queue.push(msg);
            pump();
            break;
          case 'taunt':
            listenersRef.current.onTaunt?.(msg);
            break;
          case 'phrase':
            listenersRef.current.onPhrase?.(msg);
            break;
          case 'error':
            setError({ code: msg.code, message: msg.message, at: Date.now() });
            if (HARD_ERRORS.has(msg.code)) {
              stopRef.current = true;
              setStatus('closed');
              ws.close();
            }
            break;
          case 'pong':
            setOffset(msg.now - Date.now());
            break;
        }
      };

      ws.onclose = () => {
        clearInterval(pingTimer);
        if (stopRef.current) return;
        setStatus('reconnecting');
        attempt += 1;
        const delay = Math.min(8000, 400 * 2 ** Math.min(attempt, 5)) + Math.random() * 300;
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();

    // Voltou para o app (celular tirou da tela): reconecta na hora se precisar.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && wsRef.current?.readyState !== WebSocket.OPEN && !stopRef.current) {
        clearTimeout(retryTimer);
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);

    return () => {
      stopRef.current = true;
      clearTimeout(retryTimer);
      clearInterval(pingTimer);
      timers.forEach(clearTimeout);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
      wsRef.current?.close();
    };
  }, [code, name, claim]);

  const send = useCallback((msg: ClientMsg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const act = useCallback((action: ClientAction) => send({ t: 'act', action }), [send]);
  const clearError = useCallback(() => setError(null), []);

  return { status, room, you, error, offset, rolling, holdUntil, ordering, send, act, clearError };
}

/** Relógio que anda sozinho na tela (re-renderiza a cada `ms`). */
export function useNow(ms = 250): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}
